import { useState } from "react";
import { api } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";

const ExpenseManagement = ({ expenses, projects, onUpdate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    project_name: "",
    expense_type: "supplies",
    amount: "",
    description: "",
    expense_date: "",
  });

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        project_name: expense.project_name,
        expense_type: expense.expense_type,
        amount: expense.amount.toString(),
        description: expense.description || "",
        expense_date: expense.expense_date,
      });
    } else {
      setEditingExpense(null);
      setFormData({
        project_name: projects.length > 0 ? projects[0].project_name : "",
        expense_type: "supplies",
        amount: "",
        description: "",
        expense_date: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        project_name: formData.project_name,
        expense_type: formData.expense_type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        expense_date: formData.expense_date,
      };

      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, payload);
        toast.success("Expense updated successfully");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added successfully");
      }

      onUpdate();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving expense:", error);
      toast.error("Failed to save expense");
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) {
      return;
    }

    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success("Expense deleted successfully");
      onUpdate();
    } catch (error) {
      console.error("Error deleting expense:", error);
      toast.error("Failed to delete expense");
    }
  };

  const getExpenseTypeColor = (type) => {
    const colors = {
      supplies: "border-blue-300 bg-blue-50 text-blue-700",
      mileage: "border-green-300 bg-green-50 text-green-700",
      equipment: "border-purple-300 bg-purple-50 text-purple-700",
      other: "border-slate-300 bg-slate-50 text-slate-700",
    };
    return colors[type] || colors.other;
  };

  const getExpenseTypeIcon = (type) => {
    const icons = {
      supplies: "🧴",
      mileage: "🚗",
      equipment: "🛠️",
      other: "📋",
    };
    return icons[type] || icons.other;
  };

  // Group expenses by project
  const expensesByProject = {};
  expenses.forEach(expense => {
    if (!expensesByProject[expense.project_name]) {
      expensesByProject[expense.project_name] = [];
    }
    expensesByProject[expense.project_name].push(expense);
  });

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Project Expenses
            </CardTitle>
            <CardDescription>
              Track cleaning supplies, mileage, equipment, and other expenses
            </CardDescription>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="add-expense-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-orange-900">Total Expenses</span>
            <span className="text-2xl font-bold text-orange-600">
              ${totalExpenses.toLocaleString()}
            </span>
          </div>
        </div>

        {Object.keys(expensesByProject).length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Receipt className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <p>No expenses recorded yet. Add project expenses to track costs.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(expensesByProject).map(([projectName, projectExpenses]) => {
              const projectTotal = projectExpenses.reduce((sum, e) => sum + e.amount, 0);
              
              return (
                <div key={projectName} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-slate-900">{projectName}</h4>
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                      ${projectTotal.toLocaleString()} total
                    </Badge>
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectExpenses.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell>
                            <Badge variant="outline" className={getExpenseTypeColor(expense.expense_type)}>
                              {getExpenseTypeIcon(expense.expense_type)} {expense.expense_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {expense.description || "-"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(expense.expense_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="font-semibold text-orange-600">
                            ${expense.amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenDialog(expense)}
                                data-testid={`edit-expense-${expense.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(expense.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-expense-${expense.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? "Edit Expense" : "Add New Expense"}
            </DialogTitle>
            <DialogDescription>
              Record project expenses like supplies, mileage, or equipment
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="project_name">Project *</Label>
                <Select
                  value={formData.project_name}
                  onValueChange={(value) => setFormData({ ...formData, project_name: value })}
                  disabled={editingExpense !== null}
                >
                  <SelectTrigger data-testid="expense-project-select">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.project_name}>
                        {project.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expense_type">Expense Type *</Label>
                <Select
                  value={formData.expense_type}
                  onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
                >
                  <SelectTrigger data-testid="expense-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplies">Cleaning Supplies</SelectItem>
                    <SelectItem value="mileage">Mileage</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount ($) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  data-testid="expense-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expense_date">Expense Date *</Label>
                <Input
                  id="expense_date"
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  required
                  data-testid="expense-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description / Notes</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Industrial cleaning supplies, 150 miles @ $0.57/mile"
                  rows={3}
                  data-testid="expense-description-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="expense-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="expense-save-button"
              >
                {editingExpense ? "Update" : "Add Expense"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ExpenseManagement;
